import { ArrowDown, ArrowUp } from 'lucide-react'
import { type ReactNode } from 'react'

import { TableHeader } from '@ankor-io/ui-components/src/Table'

type SortableTableHeaderProps<T extends string> = {
  label: string
  sortKey: T
  activeSortBy: T
  activeSortOrder: 'asc' | 'desc'
  onSort: (sortKey: T, sortOrder: 'asc' | 'desc') => void
  getDefaultSortOrder: (sortKey: T) => 'asc' | 'desc'
  className?: string
  /** Extra header content rendered next to the sort button, such as an info tooltip trigger. */
  children?: ReactNode
}

export const SortableTableHeader = <T extends string>({
  label,
  sortKey,
  activeSortBy,
  activeSortOrder,
  onSort,
  getDefaultSortOrder,
  className,
  children,
}: SortableTableHeaderProps<T>) => {
  const isActive = activeSortBy === sortKey

  const handleClick = () => {
    if (isActive) {
      onSort(sortKey, activeSortOrder === 'desc' ? 'asc' : 'desc')
      return
    }

    onSort(sortKey, getDefaultSortOrder(sortKey))
  }

  return (
    <TableHeader
      className={className}
      aria-sort={isActive ? (activeSortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={handleClick}
          className="cursor-pointer inline-flex items-center gap-1 font-medium text-inherit hover:text-gray-700 dark:hover:text-gray-200"
        >
          <span>{label}</span>
          {isActive &&
            (activeSortOrder === 'asc' ? (
              <ArrowUp
                className="size-4"
                aria-hidden
              />
            ) : (
              <ArrowDown
                className="size-4"
                aria-hidden
              />
            ))}
        </button>
        {children}
      </div>
    </TableHeader>
  )
}
